import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Upload, CheckCircle2, AlertCircle, ArrowDownLeft, ArrowUpRight, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";

function Inner() {
  const { activeId } = useCompany();
  const nav = useNavigate();
  const [ledgers, setLedgers] = useState([]);
  const [bankId, setBankId] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [overrides, setOverrides] = useState({});  // rowIdx -> {voucher_id, entry_idx} or "skip"

  useEffect(() => {
    if (!activeId) return;
    api.get(`/companies/${activeId}/ledgers`).then((r) => {
      const banks = (r.data || []).filter((L) => L.group === "Bank Accounts" || L.group === "Cash-in-Hand");
      setLedgers(banks);
      if (banks.length && !bankId) setBankId(banks[0].id);
    });
  }, [activeId, bankId]);

  const doUpload = async () => {
    if (!bankId) { toast.error("Choose a bank ledger first"); return; }
    if (!file) { toast.error("Select a CSV file first"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/companies/${activeId}/bank/${bankId}/import-statement`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(data);
      // Initialize overrides with auto-match results
      const init = {};
      (data.rows || []).forEach((r, idx) => {
        if (r.match) init[idx] = { voucher_id: r.match.voucher_id, entry_idx: r.match.entry_idx };
      });
      setOverrides(init);
      toast.success(`Parsed ${data.summary.total} rows · ${data.summary.auto_matched} auto-matched`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const applyMatches = async () => {
    if (!preview) return;
    const matches = [];
    (preview.rows || []).forEach((r, idx) => {
      const ov = overrides[idx];
      if (ov && ov !== "skip") {
        matches.push({
          voucher_id: ov.voucher_id,
          entry_idx: ov.entry_idx,
          statement_date: r.date,
          statement_ref: (r.description || "").slice(0, 40),
        });
      }
    });
    if (matches.length === 0) { toast.error("No matches selected"); return; }
    try {
      const { data } = await api.post(`/companies/${activeId}/bank/apply-statement-matches`, {
        ledger_id: bankId, matches,
      });
      toast.success(`${data.applied} entries reconciled. Open the Bank Reconciliation page to view.`);
      setPreview(null);
      setFile(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    }
  };

  const summary = preview?.summary || { total: 0, auto_matched: 0, unmatched: 0 };
  const matchCount = useMemo(() => Object.values(overrides).filter((v) => v && v !== "skip").length, [overrides]);

  return (
    <>
      <PageHeader
        title="Import Bank Statement"
        subtitle="Upload an HDFC / ICICI / SBI / Axis / Kotak statement CSV — we auto-match against your existing vouchers"
        actions={
          <Button variant="outline" onClick={() => nav("/banking")}><ChevronLeft className="w-4 h-4 mr-1" /> Back to Banking</Button>
        }
      />
      <div className="p-6 space-y-4" data-testid="stmt-import-page">
        {/* Upload form */}
        <div className="border border-gray-200 rounded bg-white p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="text-xs font-medium text-gray-700">Bank ledger</label>
              <select
                className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
                value={bankId}
                onChange={(e) => setBankId(e.target.value)}
                data-testid="select-bank-ledger"
              >
                {ledgers.length === 0 && <option value="">No bank ledgers found — create one first</option>}
                {ledgers.map((L) => <option key={L.id} value={L.id}>{L.name} ({L.group})</option>)}
              </select>
            </div>
            <div className="md:col-span-1">
              <label className="text-xs font-medium text-gray-700">CSV file</label>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 file:text-xs file:font-semibold"
                data-testid="input-csv-file"
              />
            </div>
            <div>
              <Button onClick={doUpload} disabled={!bankId || !file || busy} className="w-full" data-testid="btn-upload-stmt">
                <Upload className="w-4 h-4 mr-1" /> {busy ? "Parsing…" : "Upload & Auto-match"}
              </Button>
            </div>
          </div>
          <div className="mt-2 text-[11px] text-gray-500">
            Supports CSVs from HDFC, ICICI, SBI, Axis, Kotak and most Indian banks. Auto-detects columns based on common header names (Date / Narration / Withdrawal Amt / Deposit Amt / Balance).
          </div>
        </div>

        {/* Preview + match overrides */}
        {preview && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="stmt-summary">
              <div className="bg-white border border-gray-200 rounded p-3">
                <div className="text-[10px] uppercase text-gray-500">Total Rows</div>
                <div className="text-lg font-semibold">{summary.total}</div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                <div className="text-[10px] uppercase text-emerald-700">Auto-matched</div>
                <div className="text-lg font-semibold text-emerald-900">{summary.auto_matched}</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-3">
                <div className="text-[10px] uppercase text-amber-700">Unmatched</div>
                <div className="text-lg font-semibold text-amber-900">{summary.unmatched}</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <div className="text-[10px] uppercase text-blue-700">Total Inflow</div>
                <div className="text-base font-semibold font-mono text-blue-900">₹{formatINR(summary.total_inflow || 0)}</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <div className="text-[10px] uppercase text-red-700">Total Outflow</div>
                <div className="text-base font-semibold font-mono text-red-900">₹{formatINR(summary.total_outflow || 0)}</div>
              </div>
            </div>

            <div className="border border-gray-200 rounded bg-white overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <div className="text-xs font-semibold text-gray-900 uppercase tracking-wider">Match preview</div>
                <Button size="sm" onClick={applyMatches} disabled={matchCount === 0} data-testid="btn-apply-matches">
                  Apply {matchCount} matches
                </Button>
              </div>
              <table className="w-full text-xs" data-testid="stmt-rows-table">
                <thead>
                  <tr className="text-[10px] uppercase text-gray-500 border-b border-gray-200 bg-gray-50">
                    <th className="text-left pl-3 py-1.5 font-medium w-24">Stmt Date</th>
                    <th className="text-left py-1.5 font-medium">Narration</th>
                    <th className="text-right py-1.5 font-medium w-24">Withdrawal</th>
                    <th className="text-right py-1.5 font-medium w-24">Deposit</th>
                    <th className="text-left py-1.5 pl-3 font-medium w-80">Matched Voucher</th>
                    <th className="text-center py-1.5 font-medium w-24">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.rows || []).map((r, idx) => (
                    <tr key={`${r.date}-${r.description}-${r.withdrawal || 0}-${r.deposit || 0}-${idx}`} className={`border-b border-gray-100 ${overrides[idx] && overrides[idx] !== "skip" ? "bg-emerald-50/40" : ""}`} data-testid={`stmt-row-${idx}`}>
                      <td className="pl-3 py-2 font-mono text-gray-700">{r.date}</td>
                      <td className="py-2 text-gray-900">{r.description}</td>
                      <td className="py-2 pr-2 text-right font-mono text-red-700">
                        {r.withdrawal > 0 ? <><ArrowUpRight className="w-3 h-3 inline opacity-50" /> {formatINR(r.withdrawal)}</> : "—"}
                      </td>
                      <td className="py-2 pr-2 text-right font-mono text-emerald-700">
                        {r.deposit > 0 ? <><ArrowDownLeft className="w-3 h-3 inline opacity-50" /> {formatINR(r.deposit)}</> : "—"}
                      </td>
                      <td className="pl-3 py-2">
                        {r.match ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={!!overrides[idx] && overrides[idx] !== "skip"}
                              onChange={(e) => setOverrides((o) => ({ ...o, [idx]: e.target.checked ? { voucher_id: r.match.voucher_id, entry_idx: r.match.entry_idx } : "skip" }))}
                              data-testid={`stmt-row-${idx}-checkbox`}
                            />
                            <span className="font-mono text-blue-700">{r.match.voucher_no}</span>
                            <span className="text-gray-500 truncate max-w-xs">{r.match.narration}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic flex items-center gap-1"><AlertCircle className="w-3 h-3" /> No match</span>
                        )}
                      </td>
                      <td className="py-2 text-center">
                        {r.confidence === "exact" && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"><CheckCircle2 className="w-3 h-3" /> exact</span>}
                        {r.confidence === "near" && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">~near</span>}
                        {r.confidence === "none" && <span className="text-[10px] text-gray-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-[11px] text-gray-500">
              Unmatched rows can be manually entered as vouchers later — they will be available in the next import. Already-reconciled vouchers will not appear as match candidates again.
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default function BankStatementImport() {
  return <RequireCompany><Inner /></RequireCompany>;
}
